/**
 * Firebase — FCM only. Analytics disabled. All SDK loads are lazy + try/catch.
 */

export const VAPID_KEY =
  'BE7439GKN7uodJ_Iyo3-7cdOP7fIbVnah2sP82zdHdvfQAt6ynsyD7SaH6OALMjw4E_w4eWoU4KnqReWy2CcMj8';

const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || '';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain:
    process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || (projectId ? `${projectId}.firebaseapp.com` : undefined),
  projectId,
  storageBucket:
    process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || (projectId ? `${projectId}.appspot.com` : undefined),
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

/** @type {Promise<import('firebase/app').FirebaseApp | null> | null} */
let appInitPromise = null;

/** @type {Promise<import('firebase/messaging').Messaging | null> | null} */
let messagingInitPromise = null;

function isAsciiHeaderSafe(value) {
  const s = String(value ?? '');
  if (!s) return true;
  for (let i = 0; i < s.length; i += 1) {
    if (s.charCodeAt(i) > 255) return false;
  }
  return true;
}

export function isFirebaseConfigured() {
  if (
    !firebaseConfig.apiKey ||
    !firebaseConfig.projectId ||
    !firebaseConfig.messagingSenderId ||
    !firebaseConfig.appId
  ) {
    return false;
  }
  const fields = [
    firebaseConfig.apiKey,
    firebaseConfig.authDomain,
    firebaseConfig.projectId,
    firebaseConfig.storageBucket,
    firebaseConfig.messagingSenderId,
    firebaseConfig.appId,
    VAPID_KEY,
  ];
  return fields.every(isAsciiHeaderSafe);
}

export function getFirebaseConfigForSw() {
  return { ...firebaseConfig };
}

/** Analytics intentionally disabled (was causing Headers / ISO-8859-1 crashes). */
export async function getFirebaseAnalytics() {
  return null;
}

async function ensureFirebaseApp() {
  if (!isFirebaseConfigured()) return null;
  if (!appInitPromise) {
    appInitPromise = (async () => {
      try {
        const { initializeApp, getApps } = await import('firebase/app');
        if (getApps().length > 0) return getApps()[0];
        return initializeApp(firebaseConfig);
      } catch (err) {
        console.warn('[firebase] app init failed', err?.message || err);
        appInitPromise = null;
        return null;
      }
    })();
  }
  return appInitPromise;
}

/** @returns {Promise<import('firebase/app').FirebaseApp | null>} */
export async function getFirebaseApp() {
  try {
    return await ensureFirebaseApp();
  } catch (err) {
    console.warn('[firebase] getFirebaseApp failed', err?.message || err);
    return null;
  }
}

/** @returns {Promise<import('firebase/messaging').Messaging | null>} */
export async function getFirebaseMessaging() {
  if (!isFirebaseConfigured()) return null;
  if (!messagingInitPromise) {
    messagingInitPromise = (async () => {
      try {
        const { isSupported, getMessaging } = await import('firebase/messaging');
        if (!(await isSupported())) return null;
        const app = await ensureFirebaseApp();
        if (!app) return null;
        return getMessaging(app);
      } catch (err) {
        console.warn('[firebase] messaging init failed', err?.message || err);
        messagingInitPromise = null;
        return null;
      }
    })();
  }
  try {
    return await messagingInitPromise;
  } catch (err) {
    console.warn('[firebase] getFirebaseMessaging failed', err?.message || err);
    return null;
  }
}

/**
 * @param {string} vapidKey
 * @param {ServiceWorkerRegistration} serviceWorkerRegistration
 * @returns {Promise<string | null>}
 */
export async function requestFCMToken(vapidKey, serviceWorkerRegistration) {
  try {
    const messaging = await getFirebaseMessaging();
    const key = String(vapidKey || VAPID_KEY).trim();
    if (!messaging || !key) return null;
    const { getToken } = await import('firebase/messaging');
    return await getToken(messaging, {
      vapidKey: key,
      serviceWorkerRegistration,
    });
  } catch (err) {
    console.warn('[firebase] getToken failed', err?.message || err);
    return null;
  }
}

/**
 * @param {(payload: import('firebase/messaging').MessagePayload) => void} callback
 * @returns {Promise<(() => void) | null>}
 */
export async function subscribeFCMForeground(callback) {
  try {
    const messaging = await getFirebaseMessaging();
    if (!messaging) return null;
    const { onMessage } = await import('firebase/messaging');
    return onMessage(messaging, callback);
  } catch (err) {
    console.warn('[firebase] onMessage failed', err?.message || err);
    return null;
  }
}
